import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Notice } from '@rental/ui'
import { DataTable } from '../components/DataTable'
import { PendingBadge } from '../components/PendingBadge'
import { Section } from '../components/Section'
import { Facts } from '../components/ui'
import { useLocale } from '../i18n'
import { allOff, holdsWords, PERIOD_NAMES, periodWords, unfinishedWords } from '../system-privacy'

type Privacy = ApiSchemas['SystemPrivacy']
type Tally = Privacy['due']['contacts']

/** One line of what is past its date: a class off says so in every column. */
interface DueRow {
  key: string
  label: string
  tally: Tally | null
  /** Rows with no holds to tell (legacy imports): a count, nothing kept. */
  count?: number | null
}

/**
 * S8, the Owner's: what the hourly retention runs take, what keeps the rest
 * and why, what is erased but not finished, and the requests and holds that
 * stand. The words are system-privacy.ts's; the numbers the server's.
 */
export function PrivacySection({ privacy }: { privacy: Privacy }) {
  const { locale, messages: m } = useLocale()
  const copy = m.system.privacy
  const off = allOff(privacy.periods)
  const { due, lastRun, unfinished, requests, stripe } = privacy
  const rows: DueRow[] = [
    { key: 'identityDocuments', label: copy.due.identityDocuments, tally: due.identityDocuments },
    { key: 'vehiclePhotos', label: copy.due.vehiclePhotos, tally: due.vehiclePhotos },
    { key: 'contacts', label: copy.due.contacts, tally: due.contacts },
    { key: 'identities', label: copy.due.identities, tally: due.identities },
    { key: 'importRows', label: copy.due.importRows, tally: null, count: due.importRows },
  ]
  const erasable = (row: DueRow) =>
    row.tally
      ? row.tally.erasable
      : row.count === undefined || row.count === null
        ? copy.due.off
        : row.count
  return (
    <Section title={copy.title} status={off ? <PendingBadge /> : null} className="md:col-span-2">
      <p>{copy.intro}</p>
      {off ? <p>{copy.allOff}</p> : null}

      <h3 className="text-lg">{copy.periodsTitle}</h3>
      <Facts
        items={PERIOD_NAMES.map(
          (name) =>
            [copy.periods[name], periodWords(name, privacy.periods[name], locale, m)] as const,
        )}
      />

      <h3 className="text-lg">{copy.dueTitle}</h3>
      <DataTable
        label={copy.dueTitle}
        columns={[
          { header: copy.due.data, cell: (row: DueRow) => row.label, rowHeader: true },
          { header: copy.due.erasable, cell: erasable, numeric: true },
          {
            header: copy.due.held,
            cell: (row: DueRow) => (row.tally ? row.tally.held : copy.due.none),
            numeric: true,
          },
          {
            header: copy.due.why,
            cell: (row: DueRow) => (row.tally ? holdsWords(row.tally.holds, m) : copy.due.none),
          },
        ]}
        rows={rows}
        rowKey={(row) => row.key}
      />

      <h3 className="text-lg">{copy.lastRunTitle}</h3>
      {lastRun ? (
        <Facts
          items={[
            [copy.lastRun.when, formatDateTime(locale, lastRun.at)],
            [copy.lastRun.files, format(copy.lastRun.filesValue, lastRun.files)],
            [copy.lastRun.customers, format(copy.lastRun.customersValue, lastRun.customers)],
            [copy.lastRun.bytes, format(copy.lastRun.bytesValue, lastRun.bytes)],
            [copy.lastRun.importRows, lastRun.importRows],
            [copy.lastRun.replies, lastRun.replies],
            [copy.lastRun.stripe, lastRun.stripeQueued],
          ]}
        />
      ) : (
        <p className="text-text-muted">{copy.lastRun.never}</p>
      )}

      <h3 className="text-lg">{copy.unfinishedTitle}</h3>
      <Facts
        items={[
          [
            copy.unfinished.bytes,
            unfinishedWords(unfinished.bytesLeft, unfinished.oldestBytesErasedAt, locale, m),
          ],
          [
            copy.unfinished.copies,
            unfinishedWords(unfinished.copiesLeft, unfinished.oldestCopyRemovedAt, locale, m),
          ],
        ]}
      />
      <p className="text-sm text-text-muted">
        {privacy.lastOrphans
          ? format(copy.orphans.line, {
              when: formatDateTime(locale, privacy.lastOrphans.at),
              unknown: privacy.lastOrphans.unknown,
              failed: privacy.lastOrphans.failed,
            })
          : copy.orphans.never}
      </p>

      <h3 className="text-lg">{copy.requestsTitle}</h3>
      <Facts
        items={[
          [
            copy.requests.open,
            format(copy.requests.openValue, { open: requests.open, inGrace: requests.inGrace }),
          ],
          ...(requests.oldestRecordedAt
            ? [[copy.requests.oldest, formatDateTime(locale, requests.oldestRecordedAt)] as const]
            : []),
          [copy.requests.legalHolds, privacy.legalHolds],
          [copy.requests.stripe, format(copy.requests.stripeValue, stripe)],
        ]}
      />
      {stripe.dead > 0 ? (
        <Notice tone="warning">
          <p>{format(copy.requests.stripeDead, { count: stripe.dead })}</p>
        </Notice>
      ) : null}
    </Section>
  )
}
